import React, { useEffect, useReducer } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  NumberEditor,
  StripePriceEditor,
  TextAreaEditor,
} from 'shared/components/data-display/editors';
import { Title } from 'shared/components/abstract/interactables';
import FormCard from 'shared/components/FormCard';
import { createVoucherBatch } from 'shared/api/vouchers';
import { getBuildingProducts, searchBuildings } from 'shared/api/buildings';
import { getComplexProducts } from 'shared/api/complexes';
import { modelMap } from 'shared/utils';
import { VARIANTS, VOUCHER_RULES, VOUCHER_TYPES } from 'shared/constants';
import { getManagerPath } from 'shared/routes/building';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import {
  AsyncSearchableSelect,
  StaticSearchableSelect,
} from 'shared/components/select/SearchableSelects';
import ListGroupItem from 'shared/components/styled/listGroupItem';
import { DataReducerActions } from 'shared/reducers';
import { getVoucherBatchPath } from 'shared/routes/voucher';

const maxDurationInMonths = 25;

const initialState = {
  upgrades: [],
  quantity: '5',
  ruleType: VOUCHER_RULES.property,
  voucherType: VOUCHER_TYPES.billable,
  propertyManager: undefined,
  durationInMonths: '12',
  description: '',
  product: {},
  price: 0,
};

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'value':
      return {
        ...state,
        [payload.key]: payload.value,
      };
    case 'ruleType':
      return {
        ...state,
        ruleType: payload,
        selectedModel: undefined,
        upgrades: [],
        product: {},
      };
    case 'voucherType': {
      const testing = payload === 'testing';
      return {
        ...state,
        voucherType: payload,
        durationInMonths: testing ? '0' : '12',
        quantity: testing ? '1' : '5',
        price: 0,
      };
    }
    case 'upgrades':
      return {
        ...state,
        upgrades: payload,
        product: payload[0] && payload[0],
      };
    case 'clear':
      return {
        ...state,
        upgrades: [],
        product: {},
        selectedModel: undefined,
      };
    default:
      return state;
  }
}

export default function VoucherCreator() {
  const {
    request,
    isLoading,
  } = useRequest();
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    selectedModel,
    upgrades,
    product,
    quantity,
    ruleType,
    voucherType,
    durationInMonths,
    description,
    price,
    batchId, // Set after a batch is created.
    propertyManager,
  } = state;

  const { user } = useAuth();
  const {
    pushInfo,
    pushError,
  } = useNotification();

  // Fetches available upgrades / property manager when internet/complex/manager selection changes.
  useEffect(() => {
    async function fetchAvailableUpgrades() {
      let service;
      switch (ruleType) {
        case VOUCHER_RULES.property:
          service = getBuildingProducts;
          break;
        case VOUCHER_RULES.complex:
          service = getComplexProducts;
          break;
        default:
          pushError('Please select a valid Rule Type');
          return;
      }

      const result = await request(service(selectedModel.id));
      if (!result) return;

      if (result.length === 0) pushInfo('No common upgrades were found');
      dispatch({
        action: 'upgrades',
        payload: result,
      });
    }

    if (!selectedModel) {
      dispatch({ action: DataReducerActions.clear });
      return;
    }

    fetchAvailableUpgrades();
  }, [selectedModel, ruleType, request, pushInfo, pushError]);

  useEffect(() => {
    async function setManager() {
      let manager;

      if (ruleType === VOUCHER_RULES.property) {
        manager = selectedModel.manager;
      } else if (ruleType === VOUCHER_RULES.complex) {
        const searchResult = await request(searchBuildings(`?l=1&complex_id=${selectedModel.id}`));
        if (searchResult && searchResult.rows && searchResult.rows.length > 0) {
          manager = searchResult.rows[0].manager;
        } else {
          pushError(`Unable to find property manager for complex "${selectedModel.name}"`);
          return;
        }
      } else if (ruleType === VOUCHER_RULES.manager) {
        manager = selectedModel;
      }

      dispatch({
        action: 'value',
        payload: {
          key: 'propertyManager',
          value: manager,
        },
      });
    }

    if (!selectedModel) {
      dispatch({
        action: 'value',
        payload: { key: 'propertyManager' },
      });
      return;
    }

    setManager();
  }, [selectedModel, voucherType, ruleType, request, pushError]);

  if (batchId) {
    return <Redirect to={getVoucherBatchPath(batchId)} />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    const batchSize = parseInt(quantity, 10);

    if (voucherType === VOUCHER_TYPES.billable && !price) {
      pushInfo('Price is required for billable vouchers');
      return;
    }
    if (batchSize < 1) {
      pushInfo('Batch size must be greater than 0');
      return;
    }

    const result = await request(createVoucherBatch({
      quantity: batchSize,
      createdBy: user.username,
      productId: product.id,
      price,
      description,
      ruleType: ruleType.id,
      voucherType: voucherType.id,
      ...(voucherType !== VOUCHER_TYPES.testing && {
        durationInMonths: parseInt(durationInMonths, 10),
      }),
      ...(ruleType === VOUCHER_RULES.property && { propertyId: selectedModel.id }),
      ...(ruleType === VOUCHER_RULES.complex && { propertyComplexId: selectedModel.id }),
    }));
    if (!result) return;

    dispatch({
      action: 'value',
      payload: {
        key: 'batchId',
        value: result.batchId,
      },
    });
  };

  return (
    <form id="voucher-form" onSubmit={onSubmit}>
      <Title title="Service Vouchers" />
      <FormCard>
        <Grid
          container
          size={{ lg: 8, xs: 12 }}
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        >
          <Grid size={{ lg: 6, xs: 12 }}>
            <StaticSearchableSelect
              disableClearable
              getOptionValue={(o) => o.id}
              onChange={(value) => dispatch({
                action: 'voucherType',
                payload: value,
              })}
              staticOptions={Object.values(VOUCHER_TYPES)}
              label="Voucher Type"
              selectedOption={voucherType || null}
            />
            <AsyncSearchableSelect
              baseSearch="auth_method=802.1x"
              selectedOption={selectedModel}
              disabled={!ruleType.id}
              label={`Select ${modelMap[ruleType.id].title}`}
              preloadOptions={ruleType !== VOUCHER_RULES.property}
              searchRequest={modelMap[ruleType.id].searchService}
              onChange={(value) => dispatch({
                action: 'value',
                payload: {
                  key: 'selectedModel',
                  value,
                },
              })}
            />
            <NumberEditor
              setValue={(value) => dispatch({
                action: 'value',
                payload: {
                  key: 'quantity',
                  value,
                },
              })}
              label="Batch Size"
              value={quantity}
              maximum={500}
            />
            <NumberEditor
              setValue={(value) => dispatch({
                action: 'value',
                payload: {
                  key: 'durationInMonths',
                  value,
                },
              })}
              label="Service Months"
              value={durationInMonths}
              maximum={maxDurationInMonths}
              disabled={voucherType === VOUCHER_TYPES.testing}
            />
          </Grid>
          <Grid size={{ lg: 6, xs: 12 }}>
            <StaticSearchableSelect
              disableClearable
              getOptionValue={(o) => o.id}
              onChange={(value) => dispatch({
                action: 'ruleType',
                payload: value,
              })}
              staticOptions={Object.values(VOUCHER_RULES)}
              label="Rule Type"
              selectedOption={ruleType || null}
            />
            <StaticSearchableSelect
              staticOptions={upgrades}
              selectedOption={product?.id ? product : null}
              label="Choose an Upgrade"
              onChange={(value) => dispatch({
                action: 'value',
                payload: {
                  key: 'product',
                  value,
                },
              })}
              disabled={upgrades.length === 0}
              getOptionLabel={(o) => o.qos.description}
              getOptionValue={(o) => o.qos.id}
            />
            {voucherType === VOUCHER_TYPES.billable && (
            <StripePriceEditor
              label="Amount to Bill"
              value={price}
              setValue={(value) => dispatch({
                action: 'value',
                payload: {
                  key: 'price',
                  value,
                },
              })}
            />
            )}
            <TextAreaEditor
              setValue={(value) => dispatch({
                action: 'value',
                payload: {
                  key: 'description',
                  value,
                },
              })}
              label="Description"
              value={description}
            />
          </Grid>
          <Grid size={{ lg: 6, xs: 12 }}>
            <ListGroupItem>
              {voucherType === VOUCHER_TYPES.billable && propertyManager
                ? (
                  <span>
                    <strong>{'Property Manager: '}</strong>
                    <Link to={getManagerPath(propertyManager.id)}>{`${propertyManager.name} `}</Link>
                    will be billed monthly per redeemed voucher
                  </span>
                ) : <strong>Non Billable</strong>}
            </ListGroupItem>
          </Grid>
        </Grid>
        <Button
          form="voucher-form"
          variant={VARIANTS.contained}
          type="submit"
          disabled={isLoading || !product?.id}
          sx={{ mt: '5px', display: 'flex', ml: 'auto' }}
        >
          Create Service Vouchers
        </Button>
      </FormCard>
    </form>
  );
}
